import type { SevaGig } from '../../lib/types';
import { VEDIC_DIMENSIONS } from '../../lib/vedicDimensions';
import { SAMPRADAYA_OPTIONS } from '../../lib/sampradaya';
import { useGlossary } from '../../contexts/GlossaryContext';

interface Props {
  gig: SevaGig;
  onBook: (gig: SevaGig) => void;
  onMeetFirst: (gig: SevaGig) => void;
  onViewProvider: (gig: SevaGig) => void;
}

function botanicalStage(score: number | null | undefined): { label: string; icon: string } {
  if (score == null) return { label: 'बीज', icon: '🌱' };
  if (score <= 2)    return { label: 'बीज',  icon: '🌱' };
  if (score <= 4)    return { label: 'अंकुर', icon: '🌿' };
  if (score <= 6)    return { label: 'पर्ण',  icon: '🌾' };
  if (score <= 8)    return { label: 'पुष्प', icon: '🌸' };
  return              { label: 'फल',   icon: '🍎' };
}

export default function GigCard({ gig, onBook, onMeetFirst, onViewProvider }: Props) {
  const { t } = useGlossary();
  const dim = VEDIC_DIMENSIONS.find((d) => d.id === gig.dimension);
  const stage = botanicalStage(gig.marketplace_adhikara);
  const sampradaya = gig.sampradaya
    ? SAMPRADAYA_OPTIONS.find((s) => s.id === gig.sampradaya)
    : null;

  return (
    <article className="seva-gig-card" role="article" aria-label={gig.title}>
      <header className="seva-gig-card__header">
        <span className="seva-gig-card__dim-icon" title={dim?.label ?? gig.dimension}>
          {dim?.icon ?? '◎'}
        </span>
        <div className="seva-gig-card__title-wrap">
          <h3 className="seva-gig-card__title">{gig.title}</h3>
          <span className="seva-gig-card__dim-label">{dim?.label ?? gig.dimension}</span>
        </div>
      </header>

      <p className="seva-gig-card__desc">{gig.description}</p>

      <footer className="seva-gig-card__footer">
        {/* Botanical stage badge — derived from marketplace adhikara, never stored */}
        <span className="seva-gig-card__badge seva-gig-card__badge--stage" title={t('seva.gigs.adhikara.marketplace')}>
          {stage.icon} {stage.label}
        </span>

        {/* Mode badge */}
        <span className={`seva-gig-card__badge seva-gig-card__badge--mode seva-gig-card__badge--mode-${gig.mode}`}>
          {gig.mode === 'home'   ? `🏠 ${t('seva.gigs.mode.home')}`
           : gig.mode === 'remote' ? `💻 ${t('seva.gigs.mode.remote')}`
           : `🔄 ${t('seva.gigs.mode.either')}`}
        </span>

        {/* Raja-approval flag for home visits */}
        {gig.requires_raja_per_booking && (
          <span className="seva-gig-card__badge seva-gig-card__badge--raja" title={t('seva.gigs.raja.approval.tooltip')}>
            ⚖️ {t('seva.gigs.raja.approval.badge')}
          </span>
        )}

        {/* India Stack KYC placeholder badge */}
        {gig.india_stack?.aadhaar_verified ? (
          <span className="seva-gig-card__badge seva-gig-card__badge--kyc">
            🪪 {t('seva.gigs.india.stack.verified')}
          </span>
        ) : (
          <span className="seva-gig-card__badge seva-gig-card__badge--kyc seva-gig-card__badge--pending" title={t('seva.gigs.india.stack.pending.tooltip')}>
            🪪 {t('seva.gigs.india.stack.pending')}
          </span>
        )}

        {/* Sampradaya / dharmic tradition badge */}
        {sampradaya && (
          <span className="seva-gig-card__badge seva-gig-card__badge--sampradaya" title={t('seva.gigs.sampradaya.label')}>
            {sampradaya.icon} {t(sampradaya.glossaryKey)}
          </span>
        )}

        <div className="seva-gig-card__actions">
          <span className="seva-gig-card__rate">₹{gig.rate_inr.toLocaleString('en-IN')}</span>
          <button className="seva-btn seva-btn--ghost" onClick={() => onViewProvider(gig)}>
            {t('seva.gigs.provider.view')}
          </button>
          <button className="seva-btn seva-btn--secondary" onClick={() => onMeetFirst(gig)}
            title={t('seva.gigs.meet_first.tooltip')}>
            📞 {t('seva.gigs.meet_first.cta')}
          </button>
          <button className="seva-btn seva-btn--primary" onClick={() => onBook(gig)}>
            {t('seva.gigs.book.cta')}
          </button>
        </div>
      </footer>
    </article>
  );
}
